import React, { useEffect, useLayoutEffect, useState } from "react";
import { X } from "lucide-react";

const BANNER_HEIGHT_PX = 30; // must match --banner-h below

// Set the CSS variable BEFORE React mounts so the very first paint of the
// Navbar (which reads var(--banner-h, 0px)) already has the correct offset.
// This runs on the client only; guard against SSR / test envs.
if (typeof document !== "undefined" && typeof window !== "undefined") {
    try {
        const onHome = window.location.pathname === "/";
        const dismissed = sessionStorage.getItem("zenith_top_banner_dismissed") === "1";
        document.documentElement.style.setProperty(
            "--banner-h",
            (onHome && !dismissed) ? `${BANNER_HEIGHT_PX}px` : "0px"
        );
    } catch (_) { /* ignore */ }
}

/**
 * Slim ribbon shown at the very top of the Home page.
 * Announces the SELFCARE10 first-visit offer.
 * Fixed to viewport top; pushes the Navbar down via CSS variable --banner-h.
 * Dismissible per-session so returning visitors still see it.
 */
export default function HomeTopBanner() {
    const [open, setOpen] = useState(() => {
        try { return sessionStorage.getItem("zenith_top_banner_dismissed") !== "1"; }
        catch { return true; }
    });

    // useLayoutEffect runs synchronously BEFORE browser paint, so the Navbar's
    // resolved top position is correct on the very first paint — no transient overlap.
    useLayoutEffect(() => {
        const root = document.documentElement;
        if (open) {
            root.style.setProperty("--banner-h", `${BANNER_HEIGHT_PX}px`);
        } else {
            root.style.setProperty("--banner-h", "0px");
        }
    }, [open]);

    // Reset when the component unmounts (e.g., leaving Home)
    useEffect(() => {
        return () => document.documentElement.style.setProperty("--banner-h", "0px");
    }, []);

    if (!open) return null;

    const dismiss = () => {
        try { sessionStorage.setItem("zenith_top_banner_dismissed", "1"); } catch (_) { /* ignore */ }
        setOpen(false);
    };

    return (
        <div
            data-testid="home-top-banner"
            className="fixed top-0 inset-x-0 z-[60] bg-[#262422] text-[#F2EDE2]"
            style={{ height: `${BANNER_HEIGHT_PX}px` }}
        >
            <div className="max-w-[1500px] mx-auto px-3 md:px-8 h-full flex items-center justify-center gap-2 text-center text-[9.5px] md:text-[10px] tracking-[0.22em] uppercase leading-none">
                <span className="hidden md:inline text-[#c9b689]">✦</span>
                <span className="whitespace-nowrap">
                    <span className="hidden sm:inline">First visit? </span>
                    <span>Save </span>
                    <span className="text-[#c9b689] font-semibold">10% off</span>
                    <span> · code </span>
                    <span data-testid="banner-code" className="border border-[#c9b689]/50 px-1.5 py-[1px] rounded-sm text-[#c9b689]">SELFCARE10</span>
                </span>
                <span className="hidden md:inline text-[#c9b689]">✦</span>
                <button
                    onClick={dismiss}
                    aria-label="Dismiss"
                    data-testid="banner-close"
                    className="absolute right-2.5 md:right-4 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100 transition p-1"
                >
                    <X size={11} />
                </button>
            </div>
        </div>
    );
}
